import React, { memo } from "react";
import { WorkspaceSkeleton } from "@/components/ui/auth-skeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { MobileHeaderBarSkeleton } from "./MobileHeaderBarSkeleton";
import { UnifiedSidebarSkeleton } from "./UnifiedSidebarSkeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

/**
 * Modern skeleton component for the unified layout
 * Shows complete app structure with proper skeletons for all components
 */
export const UnifiedLayoutSkeleton = memo(() => {
  const isMobile = useIsMobile();
  const showMobileHeader = isMobile;
  
  // Debug logging in development
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🦴 [UnifiedLayoutSkeleton] Rendering unified skeleton with sidebar');
    }
  }, []);
  
  // Main container classes - match UnifiedLayout
  const mainClasses = cn(
    'flex-1 overflow-y-auto overflow-x-hidden min-h-0',
    showMobileHeader ? 'pt-20' : 'pt-6 lg:pt-8'
  );
  
  return (
    <div className="h-svh max-h-svh flex flex-col w-full overflow-hidden bg-gradient-to-br from-background via-muted/5 to-accent/5">
      {/* Mobile Header Skeleton - Only on mobile */}
      {showMobileHeader && <MobileHeaderBarSkeleton />}
      
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Sidebar Skeleton */}
        <UnifiedSidebarSkeleton />
        
        {/* Main Content Skeleton */}
        <main className={mainClasses}>
          <WorkspaceSkeleton />
        </main>
      </div>
      
      {/* Floating Chat Button Skeleton */}
      <div className="fixed bottom-6 right-6 z-40">
        <Skeleton 
          variant="accent" 
          shimmer
          className="h-14 w-14 rounded-full shadow-lg backdrop-blur-sm" 
        />
      </div>
    </div>
  );
});

UnifiedLayoutSkeleton.displayName = "UnifiedLayoutSkeleton";